import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Select from 'react-select';
import { useAcademicYear } from '../contexts/AcademicYearContext';
import { FiEdit, FiTrash2, FiBook, FiUserX } from 'react-icons/fi';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';
import Swal from 'sweetalert2';
import { useSelectStyles } from '../utils/selectStyles';

function formatDateFR(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

const Inscriptions = () => {
  const { selectedYear } = useAcademicYear();
  const selectStyles = useSelectStyles(); // Styles pour react-select adaptés au thème
  const [inscriptions, setInscriptions] = useState([]);
  const [eleves, setEleves] = useState([]);
  const [ouvertures, setOuvertures] = useState([]);
  const [search, setSearch] = useState('');
  const [ouvertureClasseFilter, setOuvertureClasseFilter] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    eleve_id: '',
    ouverture_classe_id: '',
    date_inscription: '',
    redoublant: false,
    validation: true,
    type_sejour: ''
  });
  const [showDispenseModal, setShowDispenseModal] = useState(false);
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [dispenses, setDispenses] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [dispenseForm, setDispenseForm] = useState({
    matiere_id: '',
    periode_id: '',
    is_annuelle: false
  });
  const [editingDispense, setEditingDispense] = useState(null);
  const [showDesinscriptionModal, setShowDesinscriptionModal] = useState(false);
  const [desinscriptions, setDesinscriptions] = useState([]);
  const [desinscriptionForm, setDesinscriptionForm] = useState({
    periode_id: '',
    date_desinscription: '',
    motif: '',
    date_reinscription: ''
  });
  const [editingDesinscription, setEditingDesinscription] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [elevesSansInscription, setElevesSansInscription] = useState([]);
  const [selectedEleves, setSelectedEleves] = useState([]);
  const [batchOuvertureClasseId, setBatchOuvertureClasseId] = useState('');
  const [batchDateInscription, setBatchDateInscription] = useState(new Date().toISOString().slice(0, 10));
  const [batchTypeSejour, setBatchTypeSejour] = useState('');
  const [loadingBatch, setLoadingBatch] = useState(false);

 useEffect(() => {
  fetchAll();
  api.get('/eleves').then(res => setEleves(res.data.data || []));
  api.get('/ouvertures-classes').then(res => {
    console.log('Ouvertures chargées:', res.data.data);
    setOuvertures(res.data.data || []);
  });
  api.get('/matieres').then(res => setMatieres(res.data.data || []));
  api.get('/periodes').then(res => setPeriodes(res.data.data || []));
}, []);

  const fetchAll = () => {
    api.get('/inscriptions').then(res => {
      console.log('Toutes les inscriptions chargées:', res.data.data);
      setInscriptions(res.data.data || []);
    });
  };

  const openAdd = () => {
    setEditing(null);
    setForm({
      eleve_id: '',
      ouverture_classe_id: '',
      date_inscription: new Date().toISOString().slice(0, 10),
      redoublant: false,
      validation: true,
      type_sejour: ''
    });
    setShowModal(true);
  };

  const openEdit = (insc) => {
    setEditing(insc);
    setForm({ ...insc });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSelect = (name, option) => {
    setForm(f => ({ ...f, [name]: option ? option.value : '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await api.put(`/inscriptions/${editing.id}`, form);
    } else {
      await api.post('/inscriptions', form);
    }
    fetchAll();
    closeModal();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette inscription ?',
      text: 'Cette action est irréversible. Toutes les données liées (notes, moyennes, absences, paiements, etc.) seront également supprimées.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/inscriptions/${id}`);
        fetchAll();
        await Swal.fire({
          icon: 'success',
          title: 'Supprimé !',
          text: "L'inscription et toutes les données liées ont été supprimées avec succès.",
          timer: 3000,
          showConfirmButton: false
        });
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Une erreur est survenue lors de la suppression de l\'inscription.';
        await Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: errorMessage,
          confirmButtonText: 'OK'
        });
      }
    }
  };

  // Fonctions pour le modal batch
  const openBatchModal = async () => {
    setShowBatchModal(true);
    setSelectedEleves([]);
    setBatchOuvertureClasseId('');
    setBatchDateInscription(new Date().toISOString().slice(0, 10));
    setBatchTypeSejour('');
    try {
      const res = await api.get('/inscriptions/eleves-sans-inscription');
      setElevesSansInscription(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des élèves sans inscription:', error);
      Swal.fire('Erreur', 'Impossible de charger les élèves sans inscription', 'error');
      setElevesSansInscription([]);
    }
  };

  const closeBatchModal = () => {
    setShowBatchModal(false);
    setSelectedEleves([]);
    setBatchOuvertureClasseId('');
    setElevesSansInscription([]);
  };

  const handleToggleEleve = (eleveId) => {
    setSelectedEleves(prev => {
      if (prev.includes(eleveId)) {
        return prev.filter(id => id !== eleveId);
      } else {
        return [...prev, eleveId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedEleves.length === elevesSansInscription.length) {
      setSelectedEleves([]);
    } else {
      setSelectedEleves(elevesSansInscription.map(e => e.id));
    }
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedEleves.length === 0) {
      Swal.fire('Attention', 'Veuillez sélectionner au moins un élève', 'warning');
      return;
    }

    if (!batchOuvertureClasseId) {
      Swal.fire('Attention', 'Veuillez sélectionner une classe', 'warning');
      return;
    }

    const result = await Swal.fire({
      title: 'Confirmer les inscriptions',
      text: `Voulez-vous inscrire ${selectedEleves.length} élève(s) dans la classe sélectionnée ?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, inscrire',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      setLoadingBatch(true);
      try {
        const data = {
          eleves_ids: selectedEleves,
          ouverture_classe_id: batchOuvertureClasseId,
          date_inscription: batchDateInscription,
          type_sejour: batchTypeSejour || null,
          validation: true,
          redoublant: false
        };

        const res = await api.post('/inscriptions/batch', data);
        
        if (res.data.success) {
          Swal.fire('Succès !', `${res.data.created} inscription(s) créée(s) avec succès.`, 'success');
          closeBatchModal();
          fetchAll();
        } else {
          Swal.fire('Erreur', res.data.message || 'Une erreur est survenue', 'error');
        }
      } catch (error) {
        console.error('Erreur lors de l\'inscription en batch:', error);
        Swal.fire('Erreur', error.response?.data?.message || 'Une erreur est survenue lors de l\'inscription en batch', 'error');
      } finally {
        setLoadingBatch(false);
      }
    }
  };

  const openDispenseModal = async (insc) => {
    setSelectedInscription(insc);
    setDispenseForm({ matiere_id: '', periode_id: '', is_annuelle: false });
    setEditingDispense(null);
    setShowDispenseModal(true);
    await fetchDispenses(insc.id);
  };

  const fetchDispenses = async (inscriptionId) => {
    try {
      const res = await api.get(`/dispenses-matieres?inscription_id=${inscriptionId}`);
      setDispenses(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des dispenses:', error);
      setDispenses([]);
    }
  };

  const handleDispenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        inscription_id: selectedInscription.id,
        matiere_id: dispenseForm.matiere_id,
        periode_id: dispenseForm.is_annuelle ? null : dispenseForm.periode_id
      };
      
      if (editingDispense) {
        await api.put(`/dispenses-matieres/${editingDispense.id}`, data);
        Swal.fire('Succès !', 'Dispense modifiée avec succès.', 'success');
      } else {
        await api.post('/dispenses-matieres', data);
        Swal.fire('Succès !', 'Dispense ajoutée avec succès.', 'success');
      }
      await fetchDispenses(selectedInscription.id);
      setDispenseForm({ matiere_id: '', periode_id: '', is_annuelle: false });
      setEditingDispense(null);
    } catch (error) {
      Swal.fire('Erreur !', error.response?.data?.message || 'Une erreur est survenue.', 'error');
    }
  };

  const handleDeleteDispense = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette dispense ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/dispenses-matieres/${id}`);
        await fetchDispenses(selectedInscription.id);
        Swal.fire('Supprimé !', 'La dispense a été supprimée.', 'success');
      } catch (error) {
        Swal.fire('Erreur !', 'Une erreur est survenue lors de la suppression.', 'error');
      }
    }
  };

  const handleEditDispense = (dispense) => {
    setEditingDispense(dispense);
    setDispenseForm({
      matiere_id: dispense.matiere_id,
      periode_id: dispense.periode_id || '',
      is_annuelle: !dispense.periode_id
    });
  };

  const openDesinscriptionModal = async (insc) => {
    setSelectedInscription(insc);
    setDesinscriptionForm({ periode_id: '', date_desinscription: new Date().toISOString().slice(0, 10), motif: '', date_reinscription: '' });
    setEditingDesinscription(null);
    setShowDesinscriptionModal(true);
    await fetchDesinscriptions(insc.id);
  };

  const fetchDesinscriptions = async (inscriptionId) => {
    try {
      const res = await api.get(`/desinscriptions-temporaires?inscription_id=${inscriptionId}`);
      setDesinscriptions(res.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des désinscriptions:', error);
      setDesinscriptions([]);
    }
  };

  const handleDesinscriptionSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        inscription_id: selectedInscription.id,
        periode_id: desinscriptionForm.periode_id,
        date_desinscription: desinscriptionForm.date_desinscription || new Date().toISOString().slice(0, 10),
        motif: desinscriptionForm.motif || null,
        date_reinscription: desinscriptionForm.date_reinscription || null
      };
      
      if (editingDesinscription) {
        await api.put(`/desinscriptions-temporaires/${editingDesinscription.id}`, data);
        Swal.fire('Succès !', 'Désinscription modifiée avec succès.', 'success');
      } else {
        await api.post('/desinscriptions-temporaires', data);
        Swal.fire('Succès !', 'Désinscription ajoutée avec succès.', 'success');
      }
      await fetchDesinscriptions(selectedInscription.id);
      setDesinscriptionForm({ periode_id: '', date_desinscription: new Date().toISOString().slice(0, 10), motif: '', date_reinscription: '' });
      setEditingDesinscription(null);
    } catch (error) {
      Swal.fire('Erreur !', error.response?.data?.message || 'Une erreur est survenue.', 'error');
    }
  };

  const handleDeleteDesinscription = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette désinscription ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      try {
        await api.delete(`/desinscriptions-temporaires/${id}`);
        await fetchDesinscriptions(selectedInscription.id);
        Swal.fire('Supprimé !', 'La désinscription a été supprimée.', 'success');
      } catch (error) {
        Swal.fire('Erreur !', 'Une erreur est survenue lors de la suppression.', 'error');
      }
    }
  };

  const handleEditDesinscription = (desinscription) => {
    setEditingDesinscription(desinscription);
    setDesinscriptionForm({
      periode_id: desinscription.periode_id,
      date_desinscription: desinscription.date_desinscription || new Date().toISOString().slice(0, 10),
      motif: desinscription.motif || '',
      date_reinscription: desinscription.date_reinscription || ''
    });
  };

  // --- Pagination locale ---
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Recherche et filtrage avancé
  const filteredInscriptions = inscriptions.filter(insc => {
    // Filtrage par année scolaire (ouverture de classe)
    const ouverture = ouvertures.find(o => o.ouverture_classe_id === insc.ouverture_classe_id);
    if (!ouverture || (selectedYear && ouverture.annee_scolaire_id !== selectedYear.id)) return false;
    // Filtrage par classe
    if (ouvertureClasseFilter) {
      console.log('Comparaison', insc.ouverture_classe_id, typeof insc.ouverture_classe_id, ouvertureClasseFilter.value, typeof ouvertureClasseFilter.value);
      if (String(insc.ouverture_classe_id) !== String(ouvertureClasseFilter.value)) {
        return false;
      }
    }
    // Recherche sur élève
    const eleve = eleves.find(e => e.id === insc.eleve_id);
    if (search) {
      const s = search.toLowerCase();
      if (
        !(eleve && (
          eleve.nom.toLowerCase().includes(s) ||
          eleve.prenom.toLowerCase().includes(s) ||
          (eleve.nom + ' ' + eleve.prenom).toLowerCase().includes(s) ||
          (eleve.matricule && eleve.matricule.toLowerCase().includes(s))
        ))
      ) {
        return false;
      }
    }
    return true;
  });
  const totalPages = Math.ceil(filteredInscriptions.length / pageSize) || 1;
  const paginatedInscriptions = filteredInscriptions.slice((page - 1) * pageSize, page * pageSize);
  console.log('Ouvertures (pour options):', ouvertures);
  // Liste des ouvertures de classes pour le filtre (une option = une ouverture de classe unique année+classe)
  // Select n'affiche que les ouvertures de classes de l'année sélectionnée
  // Correction : value = o.id (ouverture_classe_id), label = nom de la classe
  const ouverturesOptions = ouvertures
    .filter(o => selectedYear && o.annee_scolaire_id === selectedYear.id)
    .map(o => ({
      value: o.ouverture_classe_id, // id unique de l'ouverture de classe
      label:
        (typeof o.classe === 'string'
          ? o.classe
          : (o.classe?.classe || o.classe?.abrev || String(o.classe_id)))
        + (o.classe?.abrev ? ` (${o.classe.abrev})` : '')
    }));

    
    // Effectif par sexe si une classe est filtrée
    let effectifBox = null;
    if (ouvertureClasseFilter) {
    const classeNom = ouvertureClasseFilter.label;
    const total = filteredInscriptions.length;
    let nbG = 0, nbF = 0;
    filteredInscriptions.forEach(insc => {
      const eleve = eleves.find(e => e.id === insc.eleve_id);
      if (eleve && eleve.sexe) {
        if (eleve.sexe.toUpperCase() === 'M') nbG++;
        if (eleve.sexe.toUpperCase() === 'F') nbF++;
      }
    });
    effectifBox = (
      <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded text-blue-900 dark:text-blue-200 font-semibold flex items-center gap-4">
        Effectif de la {classeNom} : {total} &nbsp; - &nbsp; G({nbG}) F({nbF})
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-200">Inscriptions</h1>
        <div className="flex flex-col md:flex-row gap-2 md:items-center">
          <input
            type="text"
            placeholder="Rechercher élève..."
            className="border dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-400"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Select
            options={ouverturesOptions}
            value={ouvertureClasseFilter}
            onChange={setOuvertureClasseFilter}
            isClearable
            placeholder="Filtrer par ouverture de classe"
            classNamePrefix="react-select"
            styles={{
              ...selectStyles,
              container: base => ({ ...base, minWidth: 200 })
            }}
          />
          <ConditionalButton
            entity="inscriptions"
            permission="create"
            onClick={openAdd}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            Nouvelle inscription
          </ConditionalButton>
          <ConditionalButton
            entity="inscriptions"
            permission="create"
            onClick={openBatchModal}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 ml-2"
          >
            Inscriptions en batch
          </ConditionalButton>
        </div>
      </div>
      {effectifBox}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-2 text-gray-900 dark:text-gray-200">N° Inscr.</th>
              <th className="px-4 py-2 text-gray-900 dark:text-gray-200">Élève</th>
              <th className="px-4 py-2 text-gray-900 dark:text-gray-200">Classe</th>
              <th className="px-4 py-2 text-gray-900 dark:text-gray-200">Date</th>
              <th className="px-4 py-2 text-gray-900 dark:text-gray-200">Validation</th>
              <th className="px-4 py-2 text-gray-900 dark:text-gray-200">Type séjour</th>
              <th className="px-4 py-2 text-right text-gray-900 dark:text-gray-200">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredInscriptions.length === 0 ? (
              <tr><td colSpan="8" className="text-center py-4 text-gray-500 dark:text-gray-400">Aucune inscription</td></tr>
            ) : (
              paginatedInscriptions.map(insc => {
                const eleve = eleves.find(e => e.id === insc.eleve_id);
                const ouverture = ouvertures.find(o => o.ouverture_classe_id === insc.ouverture_classe_id);
                return (
                  <tr key={insc.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/80 transition-colors duration-150">
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-200">{insc.id}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-200">{eleve ? eleve.nom + ' ' + eleve.prenom : insc.eleve_id}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-200">{
                      ouverture
                        ? (
                            ouverture.nom && typeof ouverture.nom === 'string'
                              ? ouverture.nom
                              : (
                                  ouverture.classe && typeof ouverture.classe === 'object'
                                    ? (ouverture.classe.classe || ouverture.classe.abrev || '')
                                    : (typeof ouverture.classe === 'string' ? ouverture.classe : '')
                                )
                          )
                        : insc.ouverture_classe_id
                    }</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-200">{formatDateFR(insc.date_inscription)}</td>
                    {/* <td className="px-4 py-2 text-center">{insc.redoublant ? 'Oui' : 'Non'}</td>
                    <td className="px-4 py-2 text-center">{insc.boursier ? 'Oui' : 'Non'}</td> */}
                    <td className="px-4 py-2 text-center text-gray-900 dark:text-gray-200">{insc.validation ? 'Oui' : 'Non'}</td>
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-200">{insc.type_sejour}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex space-x-2 justify-end">
                        <ConditionalActions
                          entity="inscriptions"
                          onEdit={() => openEdit(insc)}
                          onDelete={() => handleDelete(insc.id)}
                          editIcon={FiEdit}
                          deleteIcon={FiTrash2}
                          className="flex space-x-2"
                        />
                        <ConditionalButton
                          entity="inscriptions"
                          permission="update"
                          onClick={() => openDispenseModal(insc)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                          title="Gérer les dispenses"
                        >
                          <FiBook />
                        </ConditionalButton>
                        <ConditionalButton
                          entity="inscriptions"
                          permission="update"
                          onClick={() => openDesinscriptionModal(insc)}
                          className="text-orange-600 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 transition-colors"
                          title="Gérer les désinscriptions temporaires"
                        >
                          <FiUserX />
                        </ConditionalButton>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination locale harmonisée */}
      {!showModal && totalPages > 1 && (
        <div className="flex justify-center items-center gap-1 mt-4 flex-wrap pb-4">
          <button 
            onClick={() => setPage(1)} 
            disabled={page === 1} 
            className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
          >
            Premier
          </button>
          <button 
            onClick={() => setPage(page - 1)} 
            disabled={page === 1} 
            className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
          >
            Précédent
          </button>
          {(() => {
            const pages = [];
            if (totalPages <= 10) {
              for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              for (let i = 1; i <= 5; i++) pages.push(i);
              if (page > 7 && page < totalPages - 6) {
                pages.push('...');
                for (let i = page - 1; i <= page + 1; i++) pages.push(i);
                pages.push('...');
              } else {
                pages.push('...');
              }
              for (let i = totalPages - 4; i <= totalPages; i++) {
                if (i > 5) pages.push(i);
              }
            }
            return [...new Set(pages)].map((p, idx) =>
              p === '...'
                ? <span key={"ellipsis-" + idx} className="mx-0.5 px-2 text-gray-500 dark:text-gray-400">...</span>
                : <button
                    key={p}
                    className={`px-2 py-1 rounded mx-0.5 text-sm transition-colors ${
                      p === page 
                        ? 'bg-primary-600 dark:bg-primary-500 text-white' 
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-primary-100 dark:hover:bg-primary-900 text-gray-700 dark:text-gray-200'
                    }`}
                    onClick={() => setPage(p)}
                    disabled={p === page}
                  >{p}</button>
            );
          })()}
          <button 
            onClick={() => setPage(page + 1)} 
            disabled={page === totalPages} 
            className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
          >
            Suivant
          </button>
          <button 
            onClick={() => setPage(totalPages)} 
            disabled={page === totalPages} 
            className="px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-colors"
          >
            Dernier
          </button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-lg">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">{editing ? 'Modifier' : 'Nouvelle'} inscription</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Élève *</label>
                <Select
                  options={eleves.map(e => ({ value: e.id, label: e.nom + ' ' + e.prenom }))}
                  value={eleves.find(e => e.id === form.eleve_id) ? { value: form.eleve_id, label: eleves.find(e => e.id === form.eleve_id).nom + ' ' + eleves.find(e => e.id === form.eleve_id).prenom } : null}
                  onChange={opt => handleSelect('eleve_id', opt)}
                  isClearable
                  placeholder="Sélectionner un élève"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ouverture de classe *</label>
                <Select
                  options={ouvertures
                    .filter(o => selectedYear && o.annee_scolaire_id === selectedYear.id)
                    .map(o => ({
                      value: o.ouverture_classe_id,
                      label: o.nom
                        || (typeof o.classe === 'string'
                          ? o.classe
                          : (o.classe?.classe || o.classe?.abrev || String(o.ouverture_classe_id)))
                    }))}
                  value={(() => {
                    const o = ouvertures.find(o => o.ouverture_classe_id === form.ouverture_classe_id);
                    return o && selectedYear && o.annee_scolaire_id === selectedYear.id
                      ? {
                          value: form.ouverture_classe_id,
                          label: o.nom
                            || (typeof o.classe === 'string'
                              ? o.classe
                              : (o.classe?.classe || o.classe?.abrev || String(o.ouverture_classe_id)))
                        }
                      : null;
                  })()}
                  onChange={opt => handleSelect('ouverture_classe_id', opt)}
                  isClearable
                  placeholder="Sélectionner une classe"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date d'inscription</label>
                <input type="date" name="date_inscription" value={form.date_inscription || ''} onChange={handleChange} className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2" />
              </div>
              <div className="flex gap-4 mb-4">
                <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <input type="checkbox" name="redoublant" checked={!!form.redoublant} onChange={handleChange} className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700" /> Redoublant
                </label>
                <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <input type="checkbox" name="validation" checked={!!form.validation} onChange={handleChange} className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700" /> Validé
                </label>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de séjour</label>
                <Select
                  options={[
                    { value: 'externe', label: 'Externe' },
                    { value: 'interne', label: 'Interne' },
                    { value: 'demi-pensionnaire', label: 'Demi-pensionnaire' }
                  ]}
                  value={form.type_sejour ? { value: form.type_sejour, label: form.type_sejour.charAt(0).toUpperCase() + form.type_sejour.slice(1).replace('-', ' ') } : null}
                  onChange={opt => setForm(f => ({ ...f, type_sejour: opt ? opt.value : '' }))}
                  isClearable
                  placeholder="Sélectionner le type de séjour"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600" onClick={closeModal}>Annuler</button>
                <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDispenseModal && selectedInscription && (
        <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
              Gérer les dispenses - {(() => {
                const eleve = eleves.find(e => e.id === selectedInscription.eleve_id);
                return eleve ? `${eleve.nom} ${eleve.prenom}` : 'Élève';
              })()}
            </h2>
            
            <form onSubmit={handleDispenseSubmit} className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matière *</label>
                  <Select
                    options={matieres.map(m => ({ value: m.id, label: m.lib_matiere }))}
                    value={matieres.find(m => m.id === dispenseForm.matiere_id) ? { value: dispenseForm.matiere_id, label: matieres.find(m => m.id === dispenseForm.matiere_id).lib_matiere } : null}
                    onChange={opt => setDispenseForm(f => ({ ...f, matiere_id: opt ? opt.value : '' }))}
                    isClearable
                    placeholder="Sélectionner une matière"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de dispense</label>
                  <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                    <input 
                      type="checkbox" 
                      checked={dispenseForm.is_annuelle} 
                      onChange={e => setDispenseForm(f => ({ ...f, is_annuelle: e.target.checked, periode_id: e.target.checked ? '' : f.periode_id }))} 
                      className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700" 
                    /> 
                    Dispense annuelle (tous les trimestres)
                  </label>
                </div>
                {!dispenseForm.is_annuelle && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Période</label>
                    <Select
                      options={periodes.map(p => ({ value: p.id, label: p.periode }))}
                      value={periodes.find(p => p.id === dispenseForm.periode_id) ? { value: dispenseForm.periode_id, label: periodes.find(p => p.id === dispenseForm.periode_id).periode } : null}
                      onChange={opt => setDispenseForm(f => ({ ...f, periode_id: opt ? opt.value : '' }))}
                      isClearable
                      placeholder="Sélectionner une période"
                      classNamePrefix="react-select"
                      styles={selectStyles}
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600" 
                  onClick={() => {
                    setShowDispenseModal(false);
                    setSelectedInscription(null);
                    setDispenseForm({ matiere_id: '', periode_id: '', is_annuelle: false });
                    setEditingDispense(null);
                  }}
                >
                  Fermer
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingDispense ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">Dispenses existantes</h3>
              {dispenses.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">Aucune dispense enregistrée</p>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Matière</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Période</th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                      {dispenses.map(dispense => (
                        <tr key={dispense.id}>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {dispense.matiere?.lib_matiere || 'Matière inconnue'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {dispense.is_annuelle || !dispense.periode_id ? 'Annuelle (tous les trimestres)' : (dispense.periode?.periode || 'Période inconnue')}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleEditDispense(dispense)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                title="Modifier"
                              >
                                <FiEdit />
                              </button>
                              <button
                                onClick={() => handleDeleteDispense(dispense.id)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                title="Supprimer"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDesinscriptionModal && selectedInscription && (
        <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
              Gérer les désinscriptions temporaires - {(() => {
                const eleve = eleves.find(e => e.id === selectedInscription.eleve_id);
                return eleve ? `${eleve.nom} ${eleve.prenom}` : 'Élève';
              })()}
            </h2>
            
            <form onSubmit={handleDesinscriptionSubmit} className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Période *</label>
                  <Select
                    options={periodes.map(p => ({ value: p.id, label: p.periode }))}
                    value={periodes.find(p => p.id === desinscriptionForm.periode_id) ? { value: desinscriptionForm.periode_id, label: periodes.find(p => p.id === desinscriptionForm.periode_id).periode } : null}
                    onChange={opt => setDesinscriptionForm(f => ({ ...f, periode_id: opt ? opt.value : '' }))}
                    isClearable
                    placeholder="Sélectionner une période"
                    classNamePrefix="react-select"
                    styles={selectStyles}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de désinscription</label>
                  <input 
                    type="date" 
                    value={desinscriptionForm.date_desinscription || ''} 
                    onChange={e => setDesinscriptionForm(f => ({ ...f, date_desinscription: e.target.value }))} 
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Motif</label>
                  <textarea 
                    value={desinscriptionForm.motif || ''} 
                    onChange={e => setDesinscriptionForm(f => ({ ...f, motif: e.target.value }))} 
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de réinscription (optionnelle)</label>
                  <input 
                    type="date" 
                    value={desinscriptionForm.date_reinscription || ''} 
                    onChange={e => setDesinscriptionForm(f => ({ ...f, date_reinscription: e.target.value }))} 
                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2" 
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600" 
                  onClick={() => {
                    setShowDesinscriptionModal(false);
                    setSelectedInscription(null);
                    setDesinscriptionForm({ periode_id: '', date_desinscription: new Date().toISOString().slice(0, 10), motif: '', date_reinscription: '' });
                    setEditingDesinscription(null);
                  }}
                >
                  Fermer
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingDesinscription ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">Désinscriptions temporaires existantes</h3>
              {desinscriptions.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">Aucune désinscription temporaire enregistrée</p>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Période</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Date désinscription</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Motif</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Date réinscription</th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                      {desinscriptions.map(desinscription => (
                        <tr key={desinscription.id}>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {desinscription.periode_nom || 'Période inconnue'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {formatDateFR(desinscription.date_desinscription)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {desinscription.motif || '-'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {desinscription.date_reinscription ? formatDateFR(desinscription.date_reinscription) : '-'}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleEditDesinscription(desinscription)}
                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                title="Modifier"
                              >
                                <FiEdit />
                              </button>
                              <button
                                onClick={() => handleDeleteDesinscription(desinscription.id)}
                                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                title="Supprimer"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Inscriptions en batch */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Inscriptions en batch</h2>
            <form onSubmit={handleBatchSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Classe d'inscription *</label>
                <Select
                  options={ouvertures
                    .filter(o => selectedYear && o.annee_scolaire_id === selectedYear.id)
                    .map(o => ({
                      value: o.ouverture_classe_id,
                      label: o.nom
                        || (typeof o.classe === 'string'
                          ? o.classe
                          : (o.classe?.classe || o.classe?.abrev || String(o.ouverture_classe_id)))
                    }))}
                  value={(() => {
                    const o = ouvertures.find(o => o.ouverture_classe_id === batchOuvertureClasseId);
                    return o && selectedYear && o.annee_scolaire_id === selectedYear.id
                      ? {
                          value: batchOuvertureClasseId,
                          label: o.nom
                            || (typeof o.classe === 'string'
                              ? o.classe
                              : (o.classe?.classe || o.classe?.abrev || String(o.ouverture_classe_id)))
                        }
                      : null;
                  })()}
                  onChange={opt => setBatchOuvertureClasseId(opt ? opt.value : '')}
                  isClearable
                  placeholder="Sélectionner une classe"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date d'inscription</label>
                <input 
                  type="date" 
                  value={batchDateInscription} 
                  onChange={(e) => setBatchDateInscription(e.target.value)} 
                  className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 px-3 py-2" 
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de séjour</label>
                <Select
                  options={[
                    { value: 'externe', label: 'Externe' },
                    { value: 'interne', label: 'Interne' },
                    { value: 'demi-pensionnaire', label: 'Demi-pensionnaire' }
                  ]}
                  value={batchTypeSejour ? { value: batchTypeSejour, label: batchTypeSejour.charAt(0).toUpperCase() + batchTypeSejour.slice(1).replace('-', ' ') } : null}
                  onChange={opt => setBatchTypeSejour(opt ? opt.value : '')}
                  isClearable
                  placeholder="Sélectionner le type de séjour"
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Élèves sans inscription ({elevesSansInscription.length})
                  </label>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {selectedEleves.length === elevesSansInscription.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                </div>
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 max-h-96 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                  {elevesSansInscription.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                      Aucun élève sans inscription trouvé
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {elevesSansInscription.map(eleve => (
                        <label
                          key={eleve.id}
                          className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedEleves.includes(eleve.id)}
                            onChange={() => handleToggleEleve(eleve.id)}
                            className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 mr-3"
                          />
                          <span className="text-gray-900 dark:text-gray-200">
                            {eleve.nom} {eleve.prenom}
                            {eleve.matricule && <span className="text-gray-500 dark:text-gray-400 ml-2">({eleve.matricule})</span>}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {selectedEleves.length > 0 && (
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {selectedEleves.length} élève(s) sélectionné(s)
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600" 
                  onClick={closeBatchModal}
                  disabled={loadingBatch}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loadingBatch || selectedEleves.length === 0 || !batchOuvertureClasseId}
                >
                  {loadingBatch ? 'Inscription en cours...' : `Inscrire les ${selectedEleves.length} élève(s) sélectionné(s)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inscriptions;
